import type { CharacterDraft, CharacterDraftMeta, WorldDraft } from '../../domain/types.js';
import type { Npc } from '../../types.js';
import { getWorldLibrary } from '../library.js';
import {
  bodyFromCharacterDraft,
  bodyFromNpc,
  defaultBodyForMeta,
  type CharacterBodySnapshot,
} from './characterBody.js';
import { characterDraftToNpc } from './characterHandoff.js';
import {
  clearDraftFromStorage,
  loadCharacterBodies,
  loadLaunchHandoff,
  saveCharacterBodies,
  saveLaunchHandoff,
  type WorldLaunchHandoff,
} from './draftStorage.js';

/** Re-open the live world on the character step, including the relationship list. */
export function draftForAddCharacter(handoff: WorldLaunchHandoff): WorldDraft {
  const snap = handoff.snapshot;
  return {
    schemaVersion: snap.schemaVersion,
    draftId: snap.draftId,
    createdAt: snap.acceptedAt,
    updatedAt: new Date().toISOString(),
    wizardStep: 'character-population',
    lastVisitedStep: 'character-population',
    location: snap.location,
    connected: snap.connected,
    answers: snap.answers,
    timePeriod: snap.timePeriod,
    schedule: snap.schedule,
    characterDrafts: snap.characters.map((c) => ({ ...c })),
    characterBonds: (snap.characterBonds ?? []).map((b) => ({ ...b })),
    reviewAccepted: true,
  };
}

export function bodiesForAddCharacter(handoff: WorldLaunchHandoff): Record<string, CharacterBodySnapshot> {
  const stored = loadCharacterBodies(handoff.snapshot.draftId);
  const out: Record<string, CharacterBodySnapshot> = { ...stored };
  handoff.characters.forEach((meta, i) => {
    if (out[meta.draftCharacterId]) return;
    const npc = handoff.roster[i];
    if (!npc) return;
    out[meta.draftCharacterId] = bodyFromNpc(npc, {
      species: meta.species,
      sex: meta.sex,
      ageRange: meta.ageRange,
    });
  });
  return out;
}

function bodyFor(draft: WorldDraft, character: CharacterDraft, bodies: Record<string, CharacterBodySnapshot>): CharacterBodySnapshot {
  return bodies[character.draftCharacterId]
    ?? bodyFromCharacterDraft(character)
    ?? defaultBodyForMeta(draft.draftId, {
      draftCharacterId: character.draftCharacterId,
      species: character.species ?? 'Human',
      sex: character.sex ?? 'M',
      ageRange: character.ageRange ?? 'Adult',
    });
}

function metaFor(character: CharacterDraft, body: CharacterBodySnapshot): CharacterDraftMeta {
  return {
    draftCharacterId: character.draftCharacterId,
    schemaVersion: character.schemaVersion,
    firstName: character.basic.firstName,
    lastInitial: character.basic.lastInitial,
    species: character.species ?? body.species,
    sex: character.sex ?? body.sex,
    ageRange: character.ageRange ?? body.ageRange,
    status: character.status,
    source: character.source,
    updatedAt: character.updatedAt,
  };
}

function refreshNpc(npc: Npc, character: CharacterDraft, body: CharacterBodySnapshot): Npc {
  const last = character.basic.lastInitial ? `${character.basic.lastInitial}.` : npc.lastName;
  return {
    ...npc,
    firstName: character.basic.firstName,
    lastName: last,
    palette: { ...body.palette },
    nature: { ...body.nature },
    nurture: body.nurture.map((t) => ({ ...t })),
  };
}

/** Write the character step back into the live location. Existing people keep their ids. */
export function commitCharactersToLocation(
  draft: WorldDraft,
  bodies: Record<string, CharacterBodySnapshot>,
): WorldLaunchHandoff | null {
  const handoff = loadLaunchHandoff();
  if (!handoff) return null;
  const active = draft.characterDrafts.filter((c) => c.status !== 'removed');
  const keptIds = new Set<string>();
  const characters: CharacterDraftMeta[] = [];
  const roster: Npc[] = [];
  handoff.characters.forEach((meta, i) => {
    const character = active.find((c) => c.draftCharacterId === meta.draftCharacterId);
    const npc = handoff.roster[i];
    if (!character || !npc) return;
    const body = bodyFor(draft, character, bodies);
    characters.push(metaFor(character, body));
    roster.push(refreshNpc(npc, character, body));
    keptIds.add(character.draftCharacterId);
  });
  for (const character of active) {
    if (keptIds.has(character.draftCharacterId)) continue;
    const body = bodyFor(draft, character, bodies);
    characters.push(metaFor(character, body));
    roster.push(characterDraftToNpc(character, body, roster.length, handoff.snapshot));
  }
  const next: WorldLaunchHandoff = {
    ...handoff,
    snapshot: {
      ...handoff.snapshot,
      characters: active,
      characterBonds: draft.characterBonds ?? [],
    },
    characters,
    roster,
    savedAt: new Date().toISOString(),
  };
  saveLaunchHandoff(next);
  saveCharacterBodies(draft.draftId, bodies);
  clearDraftFromStorage();
  getWorldLibrary().upsertWorld(next.snapshot, 1, next.roster);
  return next;
}
